import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router'

import { CoverPageIcon } from '@pleio/icons/cover-page'
import { InfoIcon } from '@pleio/icons/info'

import Spacer from 'js/components/Spacer/Spacer'
import HeaderLogo from 'js/magazine/components/HeaderLogo/HeaderLogo'
import { MagazineArticleViewFragment } from 'js/magazine-issue/fragments/MagazineArticleView.fragment.generated'
import { MagazineIssueViewFragment } from 'js/magazine-issue/fragments/MagazineIssueView.fragment.generated'
import TableOfContentsItem from 'js/magazine-issue/View/components/TableOfContents/TableOfContentsItem'

import { useMagazineView } from '../../MagazineViewContext'

import styles from './TableOfContents.module.css'

export const getPageOrder = (
    entity: MagazineIssueViewFragment,
    articles: MagazineArticleViewFragment[],
): string[] => {
    const { url, colophon } = entity
    const hasColophon = colophon.some((c) => c.value)

    return [
        url,
        ...articles.map((a) => a.url),
        ...(hasColophon ? [`${url}/colophon`] : []),
    ]
}

const TableOfContents = () => {
    const { t } = useTranslation()
    const location = useLocation()

    const { magazineIssue, articles } = useMagazineView()
    const { url, issueNumber, timePublished, container, colophon } =
        magazineIssue

    const hasColophon = colophon.some((c) => c.value)
    const pageOrder = getPageOrder(magazineIssue, articles)
    const currentIndex = pageOrder.indexOf(location.pathname)

    const getDirection = (targetUrl: string): 'back' | 'forward' => {
        const targetIndex = pageOrder.indexOf(targetUrl)
        return targetIndex > currentIndex ? 'forward' : 'back'
    }

    return (
        <div className={styles.wrapper}>
            <HeaderLogo
                icon={container.icon}
                title={container.title}
                number={issueNumber}
                subtitle={container.subtitle}
                date={timePublished}
                isSmall
            />
            <Spacer spacing="small" as="ul" className={styles.tocList}>
                <li>
                    <TableOfContentsItem
                        url={url}
                        title={t('entity-magazine.cover-page')}
                        icon={<CoverPageIcon />}
                        direction={getDirection(url)}
                    />
                </li>
                {articles.map(
                    ({ guid, url: articleUrl, featured, title, abstract }) => (
                        <li key={guid}>
                            <TableOfContentsItem
                                url={articleUrl}
                                title={title}
                                abstract={abstract}
                                featured={featured}
                                direction={getDirection(articleUrl)}
                            />
                        </li>
                    ),
                )}
                {hasColophon && (
                    <li>
                        <TableOfContentsItem
                            url={`${url}/colophon`}
                            title={t('entity-magazine.colophon')}
                            icon={<InfoIcon />}
                            direction={getDirection(`${url}/colophon`)}
                        />
                    </li>
                )}
            </Spacer>
        </div>
    )
}

export default TableOfContents
